import { getAuthInstance, restName } from '../firebase';
import { getBackendConfig } from './backends';
import { type MimickedResponse, secureFetch } from './secureFetchWrapper';

export const httpRequestWithToken = async ({
  url,
  method,
  payload,
}: {
  url: string;
  method: string;
  payload?: any;
}) => {
  const token = await getAuthInstance().currentUser?.getIdToken();

  if (!token) {
    throw new Error('No token available. User may not be logged in.');
  }

  const headers = {
    Authorization: `Basic ${window.btoa(`${restName}:${token}`)}`,
    'Content-Type': 'application/json',
  };

  return httpRequest({ url, headers, method, payload });
};

export const httpRequest = async ({
  url,
  headers,
  method,
  payload,
}: {
  url: string;
  headers: Record<string, string>;
  method: string;
  payload?: any;
}): Promise<Response | MimickedResponse> => {
  try {
    let res: Response | MimickedResponse;

    if (window.__TAURI__) {
      res = await secureFetch({
        url,
        method,
        headers,
        data: payload,
      });
    } else {
      res = await fetch(url, {
        method,
        headers,
        body: payload ? JSON.stringify(payload) : undefined,
      });
    }

    if (!res.ok) {
      // Handle HTTP errors - parse response and attach to error for backward compatibility
      const errorData = await res.json();
      const err: any = new Error(
        errorData.error?.error?.content?.message || errorData.msg || errorData.name || res.statusText
      );
      // Attach response info for backward compatibility with axios-style error handling
      err.response = {
        status: res.status,
        statusText: res.statusText,
        data: errorData,
      };
      err.request = { url, method, payload };
      throw err;
    }

    return res;
  } catch (err: any) {
    // With fetch API, exceptions are raised for network errors or
    // when explicitly thrown (e.g., for non-2xx status codes above).
    // Attach request details for debugging purposes.
    if (!err.request) {
      err.request = { url, method, payload };
    }
    throw err;
  }
};

export const getAvaBackendEndpoint = (): string =>
  localStorage.getItem('avaBackendUrl') || getBackendConfig('captionerUrl');

export const getScribeBackendEndpoint = (): string =>
  localStorage.getItem('scribeBackendUrl') || getBackendConfig('scribeUrl');

// TODO: this is not a modern way to use firebase functions, use httpsCallable instead
// doc: https://firebase.google.com/docs/functions/callable?gen=2nd
export const firebaseFunctionsEndpoint = getBackendConfig('firebaseFunctionsEndpoint');
export const cloudTranscriptsEndpoint = `${firebaseFunctionsEndpoint}/widgets/cloudTranscripts`;

export const smartRecapEndpoint = `${firebaseFunctionsEndpoint}/generateSmartRecap`;

export const activateOrgTrialEndpoint = `${firebaseFunctionsEndpoint}/subscriptions/me/activate-organization-trial`;

export const getSubscriptionBackendEndpoint = (): string => getBackendConfig('subscriptionUrl');

export const getSaasBackendEndpoint = (): string =>
  localStorage.getItem('saasBackendUrl') || getBackendConfig('saasUrl');

export const getSearchValueFromString = (searchString: string, key: string, _default: any) => {
  if (!searchString) {
    return _default;
  }
  const params = new URLSearchParams(searchString);
  return params.get(key) || _default;
};

export const getSearchValue = (window: any, key: string, _default: any = undefined) => {
  // I don't know why, but window.location can sometimes be undefined. This looks
  // like browser bug, but even when window.location is undefined,
  // document.location is set.
  const location = window.location || document.location;
  const searchString = location.search;
  return getSearchValueFromString(searchString, key, _default);
};

export const setSearchValue = (window: any, key: string, value: string) => {
  const location = window.location || document.location;
  const url = new URL(location.href);
  const oldUrlString = url.toString();
  const params = new URLSearchParams(url.search);

  params.set(key, value);
  url.search = params.toString();
  const newUrlString = url.toString();
  if (oldUrlString !== newUrlString) {
    window.history.replaceState(null, '', newUrlString);
  }
};

export const clearSearchValue = (window: any, key: string) => {
  const location = window.location || document.location;
  const url = new URL(location.href);
  const oldUrlString = url.toString();
  const params = new URLSearchParams(url.search);

  params.delete(key);
  url.search = params.toString();
  const newUrlString = url.toString();
  if (oldUrlString !== newUrlString) {
    window.history.replaceState(null, '', newUrlString);
  }
};

const initBackend = getSearchValue(window, 'endpoint', undefined);
if (initBackend && initBackend.endsWith('ava.me')) {
  const url = new URL(initBackend);
  if (url.hostname.endsWith('ava.me')) {
    localStorage.setItem('avaBackendUrl', initBackend);
  }
}

export const updateBackendAva = (backendId: string, backendsAva: any) => {
  localStorage.setItem('avaBackendUrl', backendsAva[backendId].url);
  window.location.replace('/');
};

export const fetchWithTimeout = async (url: string, options: RequestInit & { timeout?: number } = {}) => {
  const { timeout = 5000, ...fetchOptions } = options;

  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);

  const response = await fetch(url, {
    ...fetchOptions,
    signal: controller.signal,
  });
  clearTimeout(id);

  return response;
};
